import { useCreateLogin } from "@/api/mutations/logins";
import { useMe } from "@/api/queries/auth";
import { CountrySelect } from "@/components/country-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Message } from "@/components/ui/message";
import { UserSelect } from "@/components/user-select";
import { ROLES } from "@/constants/users";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

type FormData = {
  username: string;
  location: string;
  password: string;
  city: string;
  ownerId: number;
  totpSecretKey?: string;
};

export const CreateLoginDialog = () => {
  const [open, setOpen] = useState(false);
  const { data: me } = useMe();
  const { mutateAsync, isPending } = useCreateLogin();

  const isAdmin = me?.data.role === ROLES.ADMIN;

  const schema = useMemo(() => {
    return isAdmin
      ? z.object({
          username: z.string().min(1, "Username is required"),
          location: z.string().min(1, "Location is required"),
          city: z.string().min(1, "City is required"),
          totpSecretKey: z.string().optional(),
          password: z.string().optional(),
          ownerId: z.int({ message: "Please select login owner" }),
        })
      : z.object({
          username: z.string().min(1, "Username is required"),
          location: z.string().min(1, "Location is required"),
          city: z.string().min(1, "City is required"),
          totpSecretKey: z.string().optional(),
          password: z.string().optional(),
        });
  }, [isAdmin]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    // @ts-expect-error - it's ok
    resolver: zodResolver(schema),
    defaultValues: {
      username: "",
      location: "",
      password: "",
      city: "",
      totpSecretKey: "",
    },
  });
  const onSubmit = async (data: FormData) => {
    const ownerId = isAdmin ? data.ownerId : me?.data.id;
    if (!ownerId) {
      return toast.warning("There is no Agent selected");
    }

    mutateAsync({
      ...data,
      ownerId,
    })
      .then(() => {
        toast.success("Login created");
        setOpen(false);
        reset();
      })
      .catch((err: AxiosError<{ message: string }, unknown>) => {
        toast.error(err.response?.data?.["message"] ?? "Failed to create login");
      });
  };

  // eslint-disable-next-line
  const handler = handleSubmit(onSubmit as any);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button data-testid="create-login-trigger-btn">Create Login</Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Login</DialogTitle>
        </DialogHeader>

        <form onSubmit={handler} className="space-y-4">
          {isAdmin && (
            <div className="space-y-2">
              <Label>Agent</Label>
              <Controller
                name="ownerId"
                control={control}
                render={({ field }) => (
                  <UserSelect
                    userRole={ROLES.AGENT}
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange({
                        target: { value },
                      });
                    }}
                    placeholder="Select Owner"
                  />
                )}
              />
              {errors.ownerId && <Message variant="error">{errors.ownerId.message}</Message>}
            </div>
          )}
          <div className="space-y-2">
            <Label>Username</Label>
            <Controller
              name="username"
              control={control}
              render={({ field }) => <Input {...field} placeholder="Username" />}
            />
            {errors.username && <Message variant="error">{errors.username.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Location</Label>

            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <CountrySelect
                  placeholder="Please select location"
                  searchPlaceholder="Search location"
                  empyPlaceholder="No Location found."
                  value={field.value}
                  onSelect={(value) => {
                    field.onChange({ target: { value } });
                  }}
                />
              )}
            />
            {errors.location && <Message variant="error">{errors.location.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>City</Label>
            <Controller
              name="city"
              control={control}
              render={({ field }) => <Input {...field} placeholder="City" />}
            />
            {errors.city && <Message variant="error">{errors.city.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Password</Label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => <Input {...field} type="password" placeholder="Password" />}
            />
            {errors.password && <Message variant="error">{errors.password.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Secret Key</Label>
            <Controller
              name="totpSecretKey"
              control={control}
              render={({ field }) => <Input {...field} placeholder="TOTP Secret Key" />}
            />
            {errors.totpSecretKey && (
              <Message variant="error">{errors.totpSecretKey.message}</Message>
            )}
          </div>

          <Button className="w-full" type="submit" disabled={isPending}>
            {isPending ? "Creating..." : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
